import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useReturnReceiptSource, useRefreshSupplierReturns } from "@/hooks/use-supplier-receipt-returns";
import { ReturnReceiptSourcePanel } from "@/components/receipts/ReturnReceiptSource";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth, hasPermission } from "@/lib/auth";
import { api } from "@/lib/api";
import { EntityHistory } from "@/components/audit/EntityHistory";
import { useLocations } from "@/hooks/use-data";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, Truck, PackageCheck, Lock,
  AlertTriangle,
} from "lucide-react";
import { BRANCH_RETURN_REASONS, SUPPLIER_RETURN_REASONS } from "@shared/constants";
import { formatQtySnapshot } from "@shared/format-qty";

const AUTO_DISPOSITION: Record<string, string> = Object.fromEntries(
  BRANCH_RETURN_REASONS.filter((r) => r.autoDisposition).map((r) => [r.code, r.autoDisposition as string])
);

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 border-gray-200",
  pending_approval: "bg-yellow-100 text-yellow-800 border-yellow-200",
  approved: "bg-teal-100 text-teal-800 border-teal-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  shipped: "bg-indigo-100 text-indigo-800 border-indigo-200",
  received: "bg-purple-100 text-purple-800 border-purple-200",
  closed: "bg-green-100 text-green-800 border-green-200",
};

const TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  branch_to_warehouse: { label: "→ Warehouse", color: "bg-blue-100 text-blue-800 border-blue-200" },
  branch_to_kitchen: { label: "→ Kitchen", color: "bg-purple-100 text-purple-800 border-purple-200" },
  warehouse_to_supplier: { label: "→ Supplier", color: "bg-orange-100 text-orange-800 border-orange-200" },
  branch_to_supplier: { label: "→ Supplier", color: "bg-orange-100 text-orange-800 border-orange-200" },
};

const DISP_COLORS: Record<string, string> = {
  back_to_stock: "bg-green-100 text-green-800 border-green-200",
  write_off: "bg-red-100 text-red-800 border-red-200",
  quarantine: "bg-amber-100 text-amber-800 border-amber-200",
  credit_pending: "bg-blue-100 text-blue-800 border-blue-200",
};

const STEPS = ["draft", "pending_approval", "approved", "shipped", "received", "closed"];

const reasonLabels: Record<string, string> = {};
[...BRANCH_RETURN_REASONS, ...SUPPLIER_RETURN_REASONS].forEach((r: any) => {
  reasonLabels[r.code] = r.label;
});

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={`uppercase text-[10px] font-bold border ${STATUS_COLORS[status] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
      {t(`returnsWaste.statusLabel.${status}`, { defaultValue: status.replace(/_/g, " ") })}
    </Badge>
  );
}

function DispositionBadge({ disposition }: { disposition: string }) {
  const { t } = useTranslation();
  return (
    <Badge variant="outline" className={`uppercase text-[10px] font-bold border ${DISP_COLORS[disposition] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
      {t(`returnsWaste.dispositionLabel.${disposition}`, { defaultValue: disposition.replace(/_/g, " ") })}
    </Badge>
  );
}

function StatusTimeline({ status, rejected }: { status: string; rejected?: boolean }) {
  const { t } = useTranslation();
  const currentIdx = rejected ? 1 : STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-1">
      {STEPS.map((step, idx) => {
        const isDone = currentIdx > idx;
        const isCurrent = currentIdx === idx && !rejected;
        const isRejected = rejected && step === "pending_approval" && idx === 1;
        return (
          <div key={step} className="flex items-center">
            <div className={`flex flex-col items-center gap-0.5 min-w-[80px]`}>
              <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition-all
                ${isDone ? "bg-primary border-primary text-primary-foreground" :
                  isCurrent ? "border-primary text-primary bg-primary/10" :
                  isRejected ? "border-red-500 bg-red-100 text-red-700" :
                  "border-muted-foreground/30 text-muted-foreground/50 bg-background"}`}
              >
                {isDone ? "✓" : idx + 1}
              </div>
              <span className={`text-[9px] text-center leading-tight font-medium uppercase tracking-wide
                ${isCurrent ? "text-primary" : isRejected ? "text-red-600" : isDone ? "text-primary" : "text-muted-foreground/50"}`}>
                {isRejected ? t("returnsWaste.statusLabel.rejected") : t(`returnsWaste.statusLabel.${step}`, { defaultValue: step.replace(/_/g, " ") })}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div className={`h-0.5 w-6 flex-shrink-0 ${idx < currentIdx ? "bg-primary" : "bg-muted-foreground/20"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ReturnDetail({ embeddedId, onEmbeddedClose, readOnly = false }: { embeddedId?: string; onEmbeddedClose?: () => void; readOnly?: boolean } = {}) {
  const { t, i18n } = useTranslation();
  const text = (en: string, arabic: string) => i18n.language.startsWith("ar") ? arabic : en;
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { user, currentWorkspace, roles } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: locations = [] } = useLocations();

  const workspaceId = currentWorkspace?.id || "";
  const returnId = embeddedId || params.id;

  const canApprove = !readOnly && hasPermission(user, "returns.approve", roles);
  const canShip = !readOnly && hasPermission(user, "returns.ship", roles);
  const canShipReceiptReturn = !readOnly && hasPermission(user, "supplier_receipts.return" as any, roles);
  const canReceive = !readOnly && hasPermission(user, "returns.receive", roles);
  const canClose = !readOnly && hasPermission(user, "returns.close", roles);
  const canViewValues = hasPermission(user, "returns.view_values", roles);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [shipQtys, setShipQtys] = useState<Record<string, number>>({});
  const [shipShortageAck, setShipShortageAck] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveItems, setReceiveItems] = useState<any[]>([]);
  const [quarantineDialogItem, setQuarantineDialogItem] = useState<any | null>(null);
  const [quarantineDisposition, setQuarantineDisposition] = useState("back_to_stock");
  const [quarantineNote, setQuarantineNote] = useState("");

  const { data: ret, isLoading } = useQuery({
    queryKey: ["/api/returns", returnId, workspaceId],
    queryFn: () => returnId && workspaceId ? api.returns.get(returnId, workspaceId) : Promise.resolve(null),
    enabled: !!returnId && !!workspaceId,
  });
  const isSupplierReturn = ["branch_to_supplier", "warehouse_to_supplier"].includes(ret?.returnType || "");
  const source = useReturnReceiptSource(workspaceId, returnId || "", isSupplierReturn);
  const sourceLinked = !!source.data?.receiptId || !!(ret as any)?.supplierReceiptId;
  const shipNeedsAcknowledgement = !!source.data?.shipPreview?.requiresAcknowledgement ||
    !!source.data?.shipPreview?.lines.some(line => line.shortage === true || Number(line.shortage) > 0 || Number(line.afterQuantity) < 0);
  const refreshSupplierReturns = useRefreshSupplierReturns();

  const { data: settings } = useQuery({
    queryKey: ["/api/return-settings", workspaceId],
    queryFn: () => workspaceId ? api.returns.settings.get(workspaceId) : Promise.resolve(null),
    enabled: !!workspaceId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/returns", returnId, workspaceId] });
    queryClient.invalidateQueries({ queryKey: ["/api/returns", workspaceId] });
    if (isSupplierReturn) void refreshSupplierReturns(workspaceId);
  };

  const actionMutation = useMutation({
    mutationFn: async ({ action, payload }: { action: string; payload?: any }) => {
      if (!returnId) throw new Error("No return ID");
      switch (action) {
        case "submit": return api.returns.submit(returnId, workspaceId);
        case "approve": return api.returns.approve(returnId, workspaceId);
        case "reject": return api.returns.reject(returnId, workspaceId, payload.rejectionReason);
        case "ship": {
          if (isSupplierReturn && (source.isPending || source.isError)) throw new Error(text("Load receipt linkage before shipping.", "حمّل ارتباط الاستلام قبل الشحن."));
          if (sourceLinked) {
            if (!source.data?.shipPreview || shipNeedsAcknowledgement && !shipShortageAck) throw new Error(text("Preview stock and acknowledge shortages before shipping.", "عاين المخزون وأقر بالنقص قبل الشحن."));
            return apiRequest("POST", `/api/returns/${encodeURIComponent(returnId)}/ship?${new URLSearchParams({ workspaceId })}`, {
              items: (ret?.items || []).map((item: any) => ({ id: item.id, shippedQty: String(item.requestedQty) })),
              acknowledgeShortage: shipShortageAck,
            });
          }
          return api.returns.ship(returnId, workspaceId, payload.items);
        }
        case "receive": return api.returns.receive(returnId, workspaceId, payload.items);
        case "close": return api.returns.close(returnId, workspaceId);
        default: throw new Error(`Unknown action: ${action}`);
      }
    },
    onSuccess: (_, vars) => {
      toast({ title: t(`returnsWaste.detail.actionSuccess.${vars.action}`, { defaultValue: t("returnsWaste.detail.actionSuccessGeneric") }) });
      invalidate();
      setRejectDialogOpen(false);
      setShipDialogOpen(false);
      setReceiveDialogOpen(false);
    },
    onError: (err: any) => {
      toast({ title: t("common.error"), description: err.message || t("returnsWaste.detail.actionFailed"), variant: "destructive" });
    },
  });

  const quarantineMutation = useMutation({
    mutationFn: ({ itemId, disposition, note }: { itemId: string; disposition: string; note: string }) =>
      api.returns.resolveQuarantine(returnId!, itemId, workspaceId, { disposition, dispositionNote: note || undefined }),
    onSuccess: () => {
      toast({ title: t("returnsWaste.detail.quarantineResolved") });
      setQuarantineDialogItem(null);
      setQuarantineDisposition("back_to_stock");
      setQuarantineNote("");
      invalidate();
    },
    onError: (err: any) => {
      toast({ title: t("common.error"), description: err.message || t("returnsWaste.detail.quarantineResolveFailed"), variant: "destructive" });
    },
  });

  if (!user) return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!ret) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => embeddedId ? onEmbeddedClose?.() : navigate("/inventory/returns")}>
          <ArrowLeft className="h-4 w-4 me-1" /> {t("returnsWaste.detail.backToReturns")}
        </Button>
        <p className="text-muted-foreground">{t("returnsWaste.detail.notFound")}</p>
      </div>
    );
  }

  const items: any[] = Array.isArray(ret.items) ? ret.items : [];
  const hasQuarantineItems = items.some((i: any) => i.disposition === "quarantine" && !i.quarantineResolvedAt);
  const allLocations = locations as any[];
  const fromLoc = allLocations.find((l: any) => l.id === ret.fromLocationId);
  const toLoc = ret.toLocationId ? allLocations.find((l: any) => l.id === ret.toLocationId) : null;

  const typeConfig = TYPE_CONFIG[ret.returnType] || { label: ret.returnType, color: "bg-gray-100 text-gray-800 border-gray-200" };

  const isSelfApprovalBlocked =
    ret.createdByUserId === user.id && !(settings as any)?.allowSelfApprovalOnReturns;

  const handleOpenShip = async () => {
    if (isSupplierReturn) {
      const fresh = await source.refetch();
      if (fresh.isError || ((fresh.data?.receiptId || sourceLinked) && !fresh.data?.shipPreview)) {
        toast({ title: text("Shipping preview unavailable", "معاينة الشحن غير متاحة"), description: fresh.error?.message, variant: "destructive" });
        return;
      }
    }
    const initial: Record<string, number> = {};
    items.forEach((it: any) => { initial[it.id] = it.requestedQty; });
    setShipQtys(initial);
    setShipShortageAck(false);
    setShipDialogOpen(true);
  };

  const handleOpenReceive = () => {
    const autoDisp = AUTO_DISPOSITION[ret.reason] || "back_to_stock";
    const pre = items.map((it: any) => ({
      id: it.id,
      itemName: it.itemName,
      itemSku: it.itemSku,
      shippedQty: it.shippedQty ?? it.requestedQty,
      receivedQty: it.shippedQty ?? it.requestedQty,
      unit: it.unit,
      disposition: autoDisp,
      dispositionNote: "",
    }));
    setReceiveItems(pre);
    setReceiveDialogOpen(true);
  };

  return (
    <div className="space-y-6 max-w-4xl notranslate" translate="no">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => embeddedId ? onEmbeddedClose?.() : navigate("/inventory/returns")} data-testid="btn-back">
          <ArrowLeft className="h-4 w-4 me-1" /> {t("common.back")}
        </Button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-heading text-primary font-mono">
              {ret.returnNumber || ret.id?.slice(0, 8)}
            </h1>
            <StatusBadge status={ret.status} />
            <Badge variant="outline" className={`uppercase text-[10px] font-bold border ${typeConfig.color}`}>
              {t(`returnsWaste.typeShortLabel.${ret.returnType}`, { defaultValue: typeConfig.label })}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {fromLoc?.name || ret.fromLocationId} → {ret.supplierName || toLoc?.name || "—"}
          </p>
        </div>
      </div>

      {isSupplierReturn && source.isPending && <div className="h-24 animate-pulse rounded-md bg-muted" />}
      {isSupplierReturn && source.isError && <p role="alert" className="text-sm text-destructive">{text("Receipt linkage unavailable. Shipping is blocked until it loads.", "تعذر تحميل ارتباط الاستلام. الشحن متوقف حتى تحميله.")} {source.error.message} <Button size="sm" variant="outline" onClick={() => source.refetch()}>{text("Retry", "إعادة المحاولة")}</Button></p>}
      {source.data?.receiptId && <ReturnReceiptSourcePanel source={source.data} returnId={returnId!} workspaceId={workspaceId} status={ret.status} />}

      <Card>
        <CardContent className="pt-4 pb-3">
          <StatusTimeline status={ret.status} rejected={ret.status === "rejected"} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <Card>
          <CardContent className="pt-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("returnsWaste.detail.reason")}</span>
              <span className="font-medium text-end">{t(`returnsWaste.reasonLabel.${ret.reason}`, { defaultValue: reasonLabels[ret.reason] || ret.reason })}</span>
            </div>
            {ret.reasonNote && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground flex-shrink-0">{t("returnsWaste.detail.note")}</span>
                <span className="text-end text-muted-foreground" dir="auto">{ret.reasonNote}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("returnsWaste.detail.created")}</span>
              <span>{ret.createdAt ? format(new Date(ret.createdAt), "dd MMM yyyy HH:mm") : "—"}</span>
            </div>
          </CardContent>
        </Card>
        {ret.status === "rejected" && ret.rejectionReason && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>{t("returnsWaste.detail.rejectedLabel")}</strong> {ret.rejectionReason}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {ret.status === "draft" && (
          <Button
            variant="default"
            onClick={() => actionMutation.mutate({ action: "submit" })}
            disabled={actionMutation.isPending}
            data-testid="btn-submit"
          >
            <span className="inline-flex items-center">
              {actionMutation.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              <span>{t("returnsWaste.detail.submitForApproval")}</span>
            </span>
          </Button>
        )}

        {ret.status === "pending_approval" && canApprove && (
          <>
            {isSelfApprovalBlocked ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button variant="default" className="bg-green-600 hover:bg-green-700" disabled data-testid="btn-approve">
                      <CheckCircle2 className="h-4 w-4 me-2" /> {t("returnsWaste.detail.approve")}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{t("returnsWaste.detail.cannotApproveOwn")}</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="default"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => actionMutation.mutate({ action: "approve" })}
                disabled={actionMutation.isPending}
                data-testid="btn-approve"
              >
                {actionMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <CheckCircle2 className="h-4 w-4 me-2" />}
                {t("returnsWaste.detail.approve")}
              </Button>
            )}
            <Button
              variant="outline"
              className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={() => setRejectDialogOpen(true)}
              disabled={actionMutation.isPending}
              data-testid="btn-reject"
            >
              <XCircle className="h-4 w-4 me-2" /> {t("returnsWaste.detail.reject")}
            </Button>
          </>
        )}

        {ret.status === "approved" && (sourceLinked ? canShipReceiptReturn : canShip) && (
          <Button variant="default" onClick={handleOpenShip} disabled={actionMutation.isPending || isSupplierReturn && (source.isPending || source.isError || source.isFetching)} data-testid="btn-ship">
            <Truck className="h-4 w-4 me-2" /> {t("returnsWaste.detail.markAsShipped")}
          </Button>
        )}

        {ret.status === "shipped" && canReceive && !sourceLinked && (
          <Button variant="default" onClick={handleOpenReceive} disabled={actionMutation.isPending} data-testid="btn-receive">
            <PackageCheck className="h-4 w-4 me-2" /> {t("returnsWaste.detail.receiveInspect")}
          </Button>
        )}

        {ret.status === "received" && canClose && (
          hasQuarantineItems ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button variant="default" disabled data-testid="btn-close">
                    <Lock className="h-4 w-4 me-2" /> {t("returnsWaste.detail.closeReturn")}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{t("returnsWaste.detail.resolveBeforeClosing")}</TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant="default"
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => actionMutation.mutate({ action: "close" })}
              disabled={actionMutation.isPending}
              data-testid="btn-close"
            >
              {actionMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              {t("returnsWaste.detail.closeReturn")}
            </Button>
          )
        )}
      </div>

      {hasQuarantineItems && ret.status === "received" && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t("returnsWaste.detail.quarantineAlert")}
          </AlertDescription>
        </Alert>
      )}

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("returnsWaste.detail.items")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow>
                  <TableHead>{t("returnsWaste.detail.colItem")}</TableHead>
                  <TableHead className="text-center">{t("returnsWaste.detail.colRequested")}</TableHead>
                  {["shipped", "received", "closed"].includes(ret.status) && (
                    <TableHead className="text-center">{t("returnsWaste.detail.colShipped")}</TableHead>
                  )}
                  {["received", "closed"].includes(ret.status) && (
                    <TableHead className="text-center">{t("returnsWaste.detail.colReceived")}</TableHead>
                  )}
                  <TableHead>{t("returnsWaste.detail.colUnit")}</TableHead>
                  {["received", "closed"].includes(ret.status) && (
                    <TableHead>{t("returnsWaste.detail.colDisposition")}</TableHead>
                  )}
                  {canViewValues && <TableHead className="text-end">{t("returnsWaste.detail.colUnitValue")}</TableHead>}
                  {canViewValues && <TableHead className="text-end">{t("returnsWaste.detail.colLineTotal")}</TableHead>}
                  {ret.status === "received" && hasQuarantineItems && <TableHead>{t("common.actions")}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">{t("returnsWaste.detail.noItems")}</TableCell>
                  </TableRow>
                ) : (
                  items.map((item: any) => {
                    const isQuarantined = item.disposition === "quarantine" && !item.quarantineResolvedAt;
                    return (
                      <TableRow
                        key={item.id}
                        className={`hover:bg-muted/20 ${isQuarantined ? "bg-amber-50/50" : ""}`}
                        data-testid={`row-item-${item.id}`}
                      >
                        <TableCell>
                          <div className="font-medium text-sm" dir="auto">{item.itemName}</div>
                          {item.itemSku && <div className="text-xs text-muted-foreground font-mono">{item.itemSku}</div>}
                        </TableCell>
                        <TableCell className="text-center font-mono text-sm">{item.requestedQty}</TableCell>
                        {["shipped", "received", "closed"].includes(ret.status) && (
                          <TableCell className="text-center font-mono text-sm">{item.shippedQty ?? "—"}</TableCell>
                        )}
                        {["received", "closed"].includes(ret.status) && (
                          <TableCell className={`text-center font-mono text-sm ${item.receivedQty !== item.shippedQty ? "text-amber-600 font-semibold" : ""}`}>
                            {item.receivedQty ?? "—"}
                          </TableCell>
                        )}
                        <TableCell className="text-sm text-muted-foreground">{item.unit}</TableCell>
                        {["received", "closed"].includes(ret.status) && (
                          <TableCell>
                            {item.disposition ? (
                              <DispositionBadge disposition={item.disposition} />
                            ) : "—"}
                          </TableCell>
                        )}
                        {canViewValues && (
                          <TableCell className="text-end font-mono text-sm">
                            {item.unitValue != null ? Number(item.unitValue).toFixed(2) : "—"}
                          </TableCell>
                        )}
                        {canViewValues && (
                          <TableCell className="text-end font-mono text-sm">
                            {item.unitValue != null && item.receivedQty != null
                              ? (Number(item.unitValue) * Number(item.receivedQty)).toFixed(2)
                              : item.unitValue != null && item.requestedQty != null
                              ? (Number(item.unitValue) * Number(item.requestedQty)).toFixed(2)
                              : "—"}
                          </TableCell>
                        )}
                        {ret.status === "received" && hasQuarantineItems && (
                          <TableCell>
                            {isQuarantined && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                onClick={() => setQuarantineDialogItem(item)}
                                data-testid={`btn-resolve-quarantine-${item.id}`}
                              >
                                {t("returnsWaste.detail.resolve")}
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent data-testid="dialog-reject">
          <DialogHeader>
            <DialogTitle>{t("returnsWaste.detail.rejectReturn")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>{t("returnsWaste.detail.rejectionReason")} <span className="text-destructive">*</span></Label>
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={t("returnsWaste.detail.rejectionReasonPlaceholder")}
              dir="auto"
              data-testid="input-rejection-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button
              variant="destructive"
              disabled={!rejectionReason.trim() || actionMutation.isPending}
              onClick={() => actionMutation.mutate({ action: "reject", payload: { rejectionReason } })}
              data-testid="btn-confirm-reject"
            >
              {actionMutation.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {t("returnsWaste.detail.reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ship Dialog */}
      <Dialog open={shipDialogOpen} onOpenChange={setShipDialogOpen}>
        <DialogContent className="w-[calc(100%_-_2rem)] max-w-lg" dir={i18n.language.startsWith("ar") ? "rtl" : "ltr"} data-testid="dialog-ship">
          <DialogHeader>
            <DialogTitle>{t("returnsWaste.detail.markAsShipped")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pe-1">
            <p className="text-sm text-muted-foreground">{sourceLinked ? text("Ship all recorded quantities at their receipt cost. Quantities cannot be changed.", "اشحن كامل الكميات المسجلة بتكلفة الاستلام. لا يمكن تعديل الكميات.") : t("returnsWaste.detail.adjustShippedQtys")}</p>
            {sourceLinked && source.data?.shipPreview && <div className="space-y-2 rounded-md border p-2 text-sm">
              {source.data.shipPreview.lines.map((line, index) => <div key={index}><p className="break-words font-medium" dir="auto">{line.itemName}</p><p className="text-xs">{text("Current → after shipping", "الحالي ← بعد الشحن")}: <bdi>{line.currentQuantity} → {line.afterQuantity}</bdi> · {text("Ship", "شحن")}: <bdi>{line.quantity}</bdi></p>{(line.shortage === true || Number(line.shortage) > 0 || Number(line.afterQuantity) < 0) && <p className="text-xs text-amber-800">{text("Shortage — may leave negative stock.", "نقص — قد يؤدي إلى مخزون سالب.")}</p>}</div>)}
              {shipNeedsAcknowledgement && <label className="flex items-start gap-2"><input type="checkbox" checked={shipShortageAck} disabled={actionMutation.isPending} onChange={e => setShipShortageAck(e.target.checked)} /><span>{text("I acknowledge the shortages and negative stock.", "أقر بنقص المخزون والمخزون السالب.")}</span></label>}
            </div>}
            {items.map((item: any) => (
              <div key={item.id} className="flex items-center justify-between gap-3 p-2 rounded-md border">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" dir="auto">{item.itemName}</p>
                  {item.itemSku && <span className="block text-xs text-muted-foreground font-mono">{item.itemSku}</span>}
                  <p className="text-xs text-muted-foreground">{t("returnsWaste.detail.requestedLabel")}: {formatQtySnapshot(item.requestedQty, item.unit)}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Input
                    type="number"
                    disabled={sourceLinked || actionMutation.isPending}
                    min={0}
                    max={item.requestedQty}
                    value={shipQtys[item.id] ?? item.requestedQty}
                    onChange={(e) => setShipQtys((prev) => ({ ...prev, [item.id]: Number(e.target.value) }))}
                    className="w-20 font-mono text-end"
                    data-testid={`input-ship-qty-${item.id}`}
                  />
                  <span className="text-xs text-muted-foreground">{item.unit}</span>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShipDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button
              disabled={actionMutation.isPending || sourceLinked && (!source.data?.shipPreview || shipNeedsAcknowledgement && !shipShortageAck)}
              onClick={() => {
                const payload = items.map((it: any) => ({
                  id: it.id,
                  shippedQty: shipQtys[it.id] ?? it.requestedQty,
                }));
                actionMutation.mutate({ action: "ship", payload: { items: payload } });
              }}
              data-testid="btn-confirm-ship"
            >
              {actionMutation.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {t("returnsWaste.detail.confirmShipment")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-receive">
          <DialogHeader>
            <DialogTitle>{t("returnsWaste.detail.receiveInspect")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pe-1">
            {receiveItems.map((ri, idx) => {
              const discrepancy = ri.receivedQty !== ri.shippedQty;
              return (
                <div key={ri.id} className="p-3 rounded-md border space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-sm" dir="auto">{ri.itemName}</p>
                      {ri.itemSku && <span className="block text-xs text-muted-foreground font-mono">{ri.itemSku}</span>}
                      <p className="text-xs text-muted-foreground">{t("returnsWaste.detail.shippedLabel")}: {formatQtySnapshot(ri.shippedQty, ri.unit)}</p>
                    </div>
                    {discrepancy && (
                      <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-[10px]">
                        {t("returnsWaste.detail.discrepancy")}
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">{t("returnsWaste.detail.receivedQty")}</Label>
                      <Input
                        type="number"
                        min={0}
                        value={ri.receivedQty}
                        onChange={(e) => setReceiveItems((prev) => prev.map((r, i) => i === idx ? { ...r, receivedQty: Number(e.target.value) } : r))}
                        className="font-mono"
                        data-testid={`input-receive-qty-${ri.id}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t("returnsWaste.detail.colDisposition")}</Label>
                      <Select
                        value={ri.disposition}
                        onValueChange={(val) => setReceiveItems((prev) => prev.map((r, i) => i === idx ? { ...r, disposition: val } : r))}
                      >
                        <SelectTrigger data-testid={`select-disposition-${ri.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="back_to_stock">✅ {t("returnsWaste.dispositionLabel.back_to_stock")}</SelectItem>
                          <SelectItem value="write_off">🚫 {t("returnsWaste.dispositionLabel.write_off")}</SelectItem>
                          <SelectItem value="quarantine">🔍 {t("returnsWaste.dispositionLabel.quarantine")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {t("returnsWaste.detail.note")} {ri.disposition === "quarantine" && <span className="text-destructive">*</span>}
                    </Label>
                    <Input
                      value={ri.dispositionNote}
                      onChange={(e) => setReceiveItems((prev) => prev.map((r, i) => i === idx ? { ...r, dispositionNote: e.target.value } : r))}
                      placeholder={t("returnsWaste.detail.optionalNote")}
                      dir="auto"
                      data-testid={`input-disposition-note-${ri.id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button
              disabled={actionMutation.isPending}
              onClick={() => {
                const payload = receiveItems.map((ri) => ({
                  id: ri.id,
                  receivedQty: ri.receivedQty,
                  disposition: ri.disposition,
                  dispositionNote: ri.dispositionNote || undefined,
                }));
                actionMutation.mutate({ action: "receive", payload: { items: payload } });
              }}
              data-testid="btn-confirm-receive"
            >
              {actionMutation.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
              {t("returnsWaste.detail.confirmReceipt")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quarantine Resolution Dialog */}
      {quarantineDialogItem && (
        <Dialog open={!!quarantineDialogItem} onOpenChange={() => setQuarantineDialogItem(null)}>
          <DialogContent data-testid="dialog-quarantine-resolve">
            <DialogHeader>
              <DialogTitle>{t("returnsWaste.detail.resolveQuarantine")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p><strong>{t("returnsWaste.detail.itemLabel")}</strong> <span dir="auto">{quarantineDialogItem.itemName}</span></p>
              <p><strong>{t("returnsWaste.detail.qtyLabel")}</strong> {formatQtySnapshot(quarantineDialogItem.receivedQty, quarantineDialogItem.unit)}</p>
              <p className="text-muted-foreground">
                {t("returnsWaste.detail.inQuarantineSince", { date: quarantineDialogItem.updatedAt ? format(new Date(quarantineDialogItem.updatedAt), "dd MMM yyyy") : "—" })}
              </p>
              <div className="space-y-1.5">
                <Label>{t("returnsWaste.detail.colDisposition")} <span className="text-destructive">*</span></Label>
                <Select value={quarantineDisposition} onValueChange={setQuarantineDisposition}>
                  <SelectTrigger data-testid="select-quarantine-disposition">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="back_to_stock">✅ {t("returnsWaste.dispositionLabel.back_to_stock")}</SelectItem>
                    <SelectItem value="write_off">🚫 {t("returnsWaste.dispositionLabel.write_off")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("returnsWaste.detail.note")}</Label>
                <Input
                  value={quarantineNote}
                  onChange={(e) => setQuarantineNote(e.target.value)}
                  placeholder={t("returnsWaste.detail.optionalNote")}
                  dir="auto"
                  data-testid="input-quarantine-note"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuarantineDialogItem(null)}>{t("common.cancel")}</Button>
              <Button
                disabled={quarantineMutation.isPending}
                onClick={() => quarantineMutation.mutate({
                  itemId: quarantineDialogItem.id,
                  disposition: quarantineDisposition,
                  note: quarantineNote,
                })}
                data-testid="btn-confirm-quarantine-resolve"
              >
                {quarantineMutation.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
                {t("returnsWaste.detail.resolve")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <EntityHistory entityType="return" entityId={returnId} />
    </div>
  );
}
